import React, { useEffect, useMemo, useState } from 'react';
import { generateFallbackImageData } from '../utils/fallbackImageUtils';
import {
  COVER_FONT_WEIGHT,
  COVER_HEIGHT,
  COVER_WIDTH,
  TRACKING,
  WORD_GAP,
  coverTextX,
  coverTones,
  layoutCover,
  lineBaselines,
} from '../utils/typographicCover';

const FONT_TOKEN = '--cs-font-sans';

/** The donor typeface stack, read from the token so measuring and drawing agree. */
const donorFontStack = () => {
  try {
    return (
      getComputedStyle(document.documentElement).getPropertyValue(FONT_TOKEN).trim() || 'sans-serif'
    );
  } catch {
    return 'sans-serif';
  }
};

/**
 * Text width at a size, from the real face where the browser can measure it. Without an
 * OffscreenCanvas (jsdom, very old browsers) it estimates from an average glyph width,
 * which only changes where a line breaks, never whether a word is cut.
 */
const AVERAGE_GLYPH_EM = 0.66;
const makeMeasure = (fontStack) => {
  const ctx =
    typeof OffscreenCanvas === 'undefined' ? null : new OffscreenCanvas(1, 1).getContext('2d');
  if (!ctx) return (text, size) => text.length * size * AVERAGE_GLYPH_EM;
  return (text, size) => {
    ctx.font = `${COVER_FONT_WEIGHT} ${size}px ${fontStack}`;
    return ctx.measureText(text).width;
  };
};

/** Re-measures once the donor face has loaded, so a fallback face never sets the breaks. */
const useDonorFontReady = (fontStack) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts?.load) return undefined;
    let live = true;
    document.fonts
      .load(`${COVER_FONT_WEIGHT} 100px ${fontStack}`)
      .then(() => live && setReady(true))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [fontStack]);
  return ready;
};

/** The campaign's name set large on a brand tint (owner review, 2026-10-29). */
const TypographicCover = ({ title, tones }) => {
  const fontStack = useMemo(donorFontStack, []);
  const fontReady = useDonorFontReady(fontStack);
  const layout = useMemo(
    () => layoutCover(title, makeMeasure(fontStack)),
    [title, fontStack, fontReady],
  );
  if (!layout) return null;
  const baselines = lineBaselines(layout);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${COVER_WIDTH} ${COVER_HEIGHT}`}
      width="100%"
      height="100%"
      style={{ display: 'block' }}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      data-cover-mode={layout.mode}
    >
      <rect width={COVER_WIDTH} height={COVER_HEIGHT} fill={tones.tint} />
      {layout.lines.map((line, i) => (
        <text
          key={`${i}-${line}`}
          x={coverTextX(layout)}
          y={baselines[i]}
          fontWeight={COVER_FONT_WEIGHT}
          fontSize={layout.size}
          letterSpacing={TRACKING * layout.size}
          wordSpacing={WORD_GAP * layout.size}
          fill={i % 2 ? tones.mid : tones.core}
          style={{ fontFamily: `var(${FONT_TOKEN})` }}
        >
          {line}
        </text>
      ))}
    </svg>
  );
};

const FallbackHeaderImage = ({
  className = '',
  alt = 'Default fundraiser image: heart-shaped forest',
  style = {},
  variant = 'hosted-page', // "hosted-page" or "donation-elements"
  brandColor, // Optional brand color (hex string) to customize SVG
  title, // The campaign's name: with a brand colour, the cover sets it as type
}) => {
  const fallbackImageUrl = import.meta.env.VITE_FALLBACK_HEADER_IMAGE_URL;

  // Helper function to validate and normalize brandColor
  const validateBrandColor = (color) => {
    if (!color || typeof color !== 'string') return null;

    // Remove whitespace
    const trimmed = color.trim();
    if (!trimmed) return null;

    // Ensure it starts with #
    const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;

    // Validate hex format (3 or 6 hex digits after #)
    if (!/^#[0-9A-Fa-f]{3}$|^#[0-9A-Fa-f]{6}$/.test(normalized)) {
      return null;
    }

    return normalized;
  };

  // Choose styling based on variant
  const fallbackClasses =
    variant === 'donation-elements'
      ? 'absolute inset-0 w-full h-full object-cover rounded-t-md bg-gray-100'
      : 'absolute inset-0 w-full h-full object-cover rounded-[var(--cs-card-radius)] bg-[color:var(--cs-surface-sunken)]';

  const fallbackStyles =
    variant === 'donation-elements'
      ? { ...style }
      : {
          border: 'var(--header-image-border, 1px solid var(--cs-border-control))',
          ...style,
        };

  // If brandColor is provided and valid, render dynamic SVG
  const validBrandColor = validateBrandColor(brandColor);
  let coverPalette = null;
  if (validBrandColor && title?.trim()) {
    try {
      coverPalette = coverTones(validBrandColor);
    } catch (error) {
      console.warn('⚠️ Failed to build the typographic cover:', error);
      // Fall through to the hearts
    }
  }
  if (coverPalette) {
    return (
      <div
        className={`${fallbackClasses} ${className}`}
        style={{ ...fallbackStyles, overflow: 'hidden' }}
        {...(alt === '' ? { 'aria-hidden': true } : { role: 'img', 'aria-label': title })}
      >
        <TypographicCover title={title} tones={coverPalette} />
      </div>
    );
  }
  if (validBrandColor) {
    try {
      const { brand50, brand40, brand10, hearts } = generateFallbackImageData(validBrandColor);
      return (
        <div
          className={`${fallbackClasses} ${className}`}
          style={{
            ...fallbackStyles,
            overflow: 'hidden',
          }}
          role="img"
          aria-label={alt}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1600 900"
            width="100%"
            height="100%"
            style={{ display: 'block' }}
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              {/* A soft shadow under the heart */}
              <filter id="shadow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="18" result="s" />
                <feColorMatrix
                  in="s"
                  type="matrix"
                  values="0 0 0 0 0
                          0 0 0 0 0
                          0 0 0 0 0
                          0 0 0 .28 0"
                  result="sa"
                />
                <feOffset in="sa" dx="0" dy="18" />
                <feBlend in="SourceGraphic" mode="normal" />
              </filter>

              {/* Soft paint edge wobble */}
              <filter id="wobble" x="-20%" y="-20%" width="140%" height="140%">
                <feTurbulence
                  type="turbulence"
                  baseFrequency="0.010"
                  numOctaves="2"
                  seed="3"
                  result="t"
                />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="t"
                  scale="18"
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>

              {/* Canvas grain (paper texture) */}
              <filter id="grain" x="-20%" y="-20%" width="140%" height="140%">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.85"
                  numOctaves="2"
                  seed="9"
                  result="n"
                />
                <feColorMatrix
                  in="n"
                  type="matrix"
                  values="1 0 0 0 0
                          0 1 0 0 0
                          0 0 1 0 0
                          0 0 0 .10 0"
                  result="ng"
                />
                <feBlend in="SourceGraphic" in2="ng" mode="multiply" />
              </filter>

              {/* Heart path definition */}
              {/* 
                This path draws a smooth, balanced, symmetrical heart shape centered at (800, 500), 
                scaled for the 1600 x 900 canvas. The shape is carefully constructed with Bézier curves 
                to resemble a full and even heart silhouette.
              */}
              <path
                id="heartPath"
                d="
                  M700 240
                  C820 175 980 205 1090 320
                  C1175 410 1205 520 1135 645
                  C1065 770 890 790 755 745
                  C625 702 510 595 495 480
                  C480 365 560 285 650 260
                  C675 252 685 248 700 240
                  Z
                "
              />
            </defs>

            {/* Background - brand-10 (very light tint) */}
            <rect x="0" y="0" width="1600" height="900" fill={brand10} />

            {/* Progressively larger hearts behind - rendered from largest to smallest */}
            {hearts
              .slice()
              .reverse()
              .map((heart, index) => (
                <g
                  key={`heart-${index}`}
                  transform={`translate(800, 450) scale(${heart.scale}) translate(-800, -450)`}
                  filter={heart.scale === 1.0 ? 'url(#wobble)' : undefined}
                  opacity={heart.opacity}
                >
                  <use href="#heartPath" fill={heart.color} />
                </g>
              ))}

            {/* Heart shadow (subtle) - only on the main heart */}
            {/* <g filter="url(#shadow)" opacity=".55">
              <use href="#heartPath" fill={`hsl(${(hslValues.h - 120 + 360) % 360} 22% 18%)`}/>
            </g> */}

            {/* Subtle vignette like paint edge falloff */}
            <radialGradient id="vig" cx="50%" cy="48%" r="78%">
              <stop offset="60%" stopColor="#000" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.18" />
            </radialGradient>
            <rect x="0" y="0" width="1600" height="900" fill="url(#vig)" />

            {/* Final global grain pass */}
            <g filter="url(#grain)" opacity=".95">
              <rect x="0" y="0" width="1600" height="900" fill="transparent" />
            </g>
          </svg>
        </div>
      );
    } catch (error) {
      console.warn('⚠️ Failed to generate dynamic SVG fallback image:', error, {
        brandColor,
        validBrandColor,
        errorMessage: error.message,
        errorStack: error.stack,
      });
      // Fall through to static image fallback
    }
  } else if (brandColor) {
    // brandColor was provided but invalid - log for debugging
    console.warn('⚠️ Invalid brandColor provided to FallbackHeaderImage:', {
      brandColor,
      type: typeof brandColor,
      value: brandColor,
    });
  }

  // Fallback to static image if brandColor not provided or conversion failed
  if (!fallbackImageUrl) {
    console.warn('⚠️ VITE_FALLBACK_HEADER_IMAGE_URL is not configured!');
  }

  return (
    <img
      src={fallbackImageUrl}
      alt={alt}
      className={`${fallbackClasses} ${className}`}
      style={fallbackStyles}
      loading="eager"
      decoding="async"
    />
  );
};

export default FallbackHeaderImage;
